/**
 * Search utilities
 */
import _ from 'lodash';
import Fuse from 'fuse.js';


function getCombinedValues(match) {
  // Same values can exist multiple times, take all unique
  // combine all matche indices into one list
  return match.reduce((acc, value) => {
    if (value in acc) {
      return acc;
    }
    else {
      return acc.concat(value.value);
    }
  }, []);
}

/**
 * If multiple searches produce results from same key, they are found from different match objects.
 * Combine them so that one field can be highlighted in one run.
 *
 * Matches has a list of lists of pairs for indices.
 *
 * <pre><code>
 *  matches: [
 {
   arrayIndex: 0,
   indices: [[0, 3], [4, 6], [8, 10]],
   key: "title",
   value: "Erikoishammaslääkäri, protetiikka ja purentafysiologia"
 },
 {
   indices:[[10, 40]],
   value: "Janne",
   key: "first_name",
   arrayIndex: 0
 },
 {
   arrayIndex: 0,
   indices: [[1, 4]],
   key: "title",
   value: "Erikoishammaslääkäri, protetiikka ja purentafysiologia"
 },
 ]</pre></code>

 * Return value would be:
 <pre><code>
 matches: {
 title: {
   indices: [[0, 6], [8, 10]],
   key: "title",
   value: "Erikoishammaslääkäri, protetiikka ja purentafysiologia"
 },
 {
   indices:[[10, 40]],
   value: "Janne",
   key: "first_name",
   arrayIndex: 0
 },
 }</pre></code>
 *
 * @param {Array} matchesArray Matches array in Fuse.js format
 * @return {Object} Matches keyed by match key containing list of index pairs with merged result values.
 */
function combineMatches(matchesArray) {
  // Combine same match keys together
  const matches = _.groupBy(matchesArray, 'key');

  const final = {};
  for (const key in matches) {
    const match = matches[key];
    const result = {
      key: match[0].key,
    };
    // And combine each keys' matches together
    result.values = getCombinedValues(match);
    final[key] = result;
  }

  return final;
}


/**
 * Highlight an objects parameter by wrapping the matches in
 * `<span class="search-highlight"></span>`
 *
 * @param {Object} searcheable_obj An object that contains a Fuse.js matches in `matches` property
 * @param {String} field that should searched
 * @return {String} Highlighted text or input text if matches did not exist.
 */
function highlightField(searcheable_obj, field) {

  if (field in searcheable_obj.restOfMatches) {
    // if matches contained the searched value
    const matched_filters = searcheable_obj.restOfMatches[field];

    let output = "";
    let index = 0;
    matched_filters.indices.forEach((indices) => {
      // Take everything between the last index and current start
      output += wrapInHighlight(searcheable_obj[field], indices, index);
      index = indices[1] + 1;
    });
    // Append "rest" on the last iteration
    output += searcheable_obj[field].slice(index);

    return output;
  }

  return searcheable_obj[field];
}

function wrapInHighlight(input, indices, startIndex) {
  return input.slice(startIndex, indices[0]) + '<span class="search-highlight">' + input.slice(indices[0], indices[1] + 1) + '</span>';
}

/**
 * Get searchable terms from given string.
 *
 * @param {String} searchText Search input text
 * @return {Array} Terms to search for. Empty list if no terms are found
 */
function getSearchTerms(searchText) {
  if (searchText && searchText.length > 0) {
    // Get search terms split by space discarding multiple spaces
    return searchText.split(' ').filter(t => t.length > 2);
  }
  return [];
}

/**
 * Make a multi term Fuse.js search. Combines results from multiple search terms
 * into one result keyed by Fuse item key property (object id).
 *
 * Matches of same key are concatenated and result scores are multiplied together
 * to create the effect where multiple matches lower the score as score should
 * be between 0 and 1.
 *
 * @param {Array} searchTerms Terms to search for
 * @param {Fuse} searchObject Fuse.js search object
 * @return {Object} Search results in object format
 */
function doSearch(searchTerms, searchObject) {

  // Flatmap combines lists of lists to a single list
  const searchResults =  _.flatMap(searchTerms, term => {
    return searchObject instanceof Fuse ? searchObject.search(term) : {};
  });

  // Merge same matches and scores.
  return searchResults.reduce((acc, item) => {
    if (acc[item.item] != null) {
      // multiply scores so that multiple matches lower the match score
      acc[item.item]['score'] *= item.score;
      acc[item.item]['matches'] = acc[item.item]['matches'].concat(item.matches);
      acc[item.item]['matchCount']++;
    }
    else {
      acc[item.item] = item;
      acc[item.item]['matchCount'] = 1;
    }

    return acc;
  }, {});
}


export {
  combineMatches,
  highlightField,
  getSearchTerms,
  doSearch,
}



// WEBPACK FOOTER //
// ./src/app/common/search.js